import React, { useEffect, useState } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import useAuth from '@contexts/useAuth';
import useSnackbar from '@hooks/useSnackbar';
import {
  billingGetOverview,
  billingPurchaseCredits,
  billingGetPortal,
} from '@api/billing';
import type { BillingOverview } from '@api/billing';
import Dialog, { DIALOG_SIZE_VARIANT_MEDIUM } from '@components/2-molecules/Dialog';
import PlanPicker from '@components/PlanPicker/PlanPicker';
import PageHeader, {
  PAGE_HEADER_VARIANT_SMALL,
} from '@components/2-molecules/PageHeader';
import SettingInfo from '@components/2-molecules/SettingInfo';
import HorizontalDivider from '@components/1-atoms/HorizontalDivider';
import Button, {
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_OUTLINE,
} from '@components/1-atoms/Button';
import { Frame } from '../Settings.styled';
import TransactionHistory from './TransactionHistory';
import {
  Wrapper,
  BalanceHeader,
  BalanceAmount,
  BalanceUnit,
  BatchSection,
  BatchSectionLabel,
  BatchRow,
  BatchLeft,
  BatchType,
  BatchDate,
  BatchInfo,
  PurchaseForm,
  QuantityRow,
  QuantityLabel,
  QuantityInput,
  QuantityCost,
  StatusRow,
  StatusLabel,
  StatusBadge,
  CancellationBanner,
  WarningText,
  InfoText,
  SectionDescription,
  ButtonRow,
} from './BillingSettings.styled';

const CREDIT_PRICE_CENTS = 100;
const MIN_QUANTITY = 20;
const MAX_QUANTITY = 5000;

function clampQuantity(raw: number): number {
  if (!Number.isFinite(raw)) return MIN_QUANTITY;
  return Math.max(MIN_QUANTITY, Math.min(MAX_QUANTITY, Math.round(raw)));
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const SOURCE_TYPE_KEYS: Record<string, { id: string; defaultMessage: string }> = {
  SUBSCRIPTION: { id: 'billing.sourceType.subscription', defaultMessage: 'Subscription' },
  PURCHASE: { id: 'billing.sourceType.purchase', defaultMessage: 'Purchased' },
  ADMIN_GRANT: { id: 'billing.sourceType.adminGrant', defaultMessage: 'Admin Grant' },
  MIGRATED_GRANT: { id: 'billing.sourceType.migratedGrant', defaultMessage: 'Migrated' },
};

function getPlanLabel(planType: string, planTierId: string | null | undefined, intl: IntlShape): string {
  if (planType === 'NONE') return intl.formatMessage({ id: 'billing.plan.none', defaultMessage: 'No plan' });
  if (planType === 'FREE') return intl.formatMessage({ id: 'billing.plan.free', defaultMessage: 'Free' });
  if (planType === 'MIGRATED_FREE') return intl.formatMessage({ id: 'billing.plan.migrated_free', defaultMessage: 'Free (Legacy)' });
  if (planType === 'BENCHMARK') return intl.formatMessage({ id: 'billing.plan.benchmark', defaultMessage: 'Benchmark' });
  if (planType === 'STARTER') return intl.formatMessage({ id: 'billing.plan.starter', defaultMessage: 'Starter' });
  if (planType === 'BUSINESS') {
    const tierLabel = planTierId
      ? ` (${intl.formatMessage({ id: `billing.plan.tier.${planTierId}`, defaultMessage: planTierId })})`
      : '';
    return `${intl.formatMessage({ id: 'billing.plan.business', defaultMessage: 'Business' })}${tierLabel}`;
  }
  return planTierId ? `${planType} (${planTierId})` : planType;
}

const BillingSettings: React.FC = () => {
  const intl = useIntl();
  const { user } = useAuth();
  const { showError } = useSnackbar();

  const [quantity, setQuantity] = useState(MIN_QUANTITY);
  const [purchasing, setPurchasing] = useState(false);
  const [showUpgradeView, setShowUpgradeView] = useState(false);

  const closeUpgradeView = () => setShowUpgradeView(false);

  const overviewQuery = useQuery<BillingOverview>({
    queryKey: ['billingOverview', user?.id],
    queryFn: () => billingGetOverview(user!.token),
    enabled: !!user?.token,
  });

  useEffect(() => {
    if (overviewQuery.isError) {
      showError(intl.formatMessage({
        id: 'billing.error.loadOverview',
        defaultMessage: 'Failed to load billing information',
      }));
    }
  }, [overviewQuery.isError, showError, intl]);

  const handlePurchase = () => {
    if (!user?.token) return;
    const clamped = clampQuantity(quantity);
    setPurchasing(true);
    billingPurchaseCredits(user.token, clamped)
      .then(({ checkoutUrl }) => {
        globalThis.location.href = checkoutUrl;
      })
      .catch((err: unknown) => {
        showError(
          err instanceof Error
            ? err.message
            : intl.formatMessage({ id: 'billing.error.purchaseFailed', defaultMessage: 'Failed to start purchase' }),
        );
        setPurchasing(false);
      });
  };

  const handleManageSubscription = () => {
    if (!user?.token) return;
    billingGetPortal(user.token)
      .then(({ portalUrl }) => {
        globalThis.location.href = portalUrl;
      })
      .catch((err: unknown) => {
        showError(
          err instanceof Error
            ? err.message
            : intl.formatMessage({ id: 'billing.error.portalFailed', defaultMessage: 'Failed to open billing portal' }),
        );
      });
  };

  const sourceTypeLabel = (sourceType: string): string => {
    const msg = SOURCE_TYPE_KEYS[sourceType];
    return msg ? intl.formatMessage(msg) : sourceType;
  };

  if (overviewQuery.isLoading) {
    return (
      <Wrapper>
        <PageHeader
          variant={PAGE_HEADER_VARIANT_SMALL}
          title={intl.formatMessage({ id: 'billing.plan.title', defaultMessage: 'Current Plan' })}
        />
        <Frame>
          <SettingInfo
            label={intl.formatMessage({ id: 'billing.loading', defaultMessage: 'Loading billing information...' })}
          />
        </Frame>
      </Wrapper>
    );
  }

  const overview = overviewQuery.data;
  if (!overview) {
    return (
      <Wrapper>
        <PageHeader
          variant={PAGE_HEADER_VARIANT_SMALL}
          title={intl.formatMessage({ id: 'billing.title', defaultMessage: 'Billing' })}
        />
        <Frame>
          <SettingInfo
            label={intl.formatMessage({ id: 'billing.loadFailed', defaultMessage: 'Unable to load billing information.' })}
          />
        </Frame>
      </Wrapper>
    );
  }

  const isValidQuantity = Number.isFinite(quantity) && quantity >= MIN_QUANTITY && quantity <= MAX_QUANTITY;
  const batchLimitReached = overview.activePurchaseBatchCount >= overview.maxPurchaseBatches;
  const isCancelling = overview.cancelAtPeriodEnd != null;
  const planLabel = getPlanLabel(overview.planType, overview.planTierId, intl);
  const statusLabel = isCancelling
    ? intl.formatMessage({ id: 'billing.status.cancelling', defaultMessage: 'Cancelling' })
    : overview.billingStatus;

  return (
    <Wrapper data-testid="billing-settings">
      {isCancelling && (
        <CancellationBanner>
          {intl.formatMessage(
            {
              id: 'billing.cancellation.banner',
              defaultMessage:
                'Your subscription is scheduled to cancel on <b>{date}</b>. You will retain access until then.',
            },
            {
              date: formatDate(overview.cancelAtPeriodEnd),
              b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
            },
          )}
        </CancellationBanner>
      )}

      <PageHeader
        variant={PAGE_HEADER_VARIANT_SMALL}
        title={intl.formatMessage({ id: 'billing.plan.title', defaultMessage: 'Current Plan' })}
      />
      <Frame>
        <StatusRow>
          <StatusLabel>
            {intl.formatMessage({ id: 'billing.plan.status', defaultMessage: 'Status' })}
          </StatusLabel>
          <StatusBadge $status={isCancelling ? 'CANCELLING' : overview.billingStatus}>
            {statusLabel}
          </StatusBadge>
        </StatusRow>

        <HorizontalDivider middleInset />

        <SettingInfo
          label={intl.formatMessage({ id: 'billing.plan.label', defaultMessage: 'Plan' })}
          infoText={planLabel}
        />
        {overview.billingPeriod && (
          <>
            <HorizontalDivider middleInset />
            <SettingInfo
              label={intl.formatMessage({ id: 'billing.plan.billingPeriod', defaultMessage: 'Billing Period' })}
              infoText={overview.billingPeriod}
            />
          </>
        )}
        {overview.currentPeriodEnd && (
          <>
            <HorizontalDivider middleInset />
            <SettingInfo
              label={intl.formatMessage({ id: 'billing.plan.currentPeriod', defaultMessage: 'Current Period' })}
              infoText={`${formatDate(overview.currentPeriodStart)} — ${formatDate(overview.currentPeriodEnd)}`}
            />
          </>
        )}
        <ButtonRow>
          {overview.hasStripeSubscription ? (
            <Button variant={BUTTON_VARIANT_OUTLINE} onClick={handleManageSubscription} stretch={false}>
              {intl.formatMessage({ id: 'billing.plan.manageSubscription', defaultMessage: 'Manage Subscription' })}
            </Button>
          ) : (
            <Button variant={BUTTON_VARIANT_PRIMARY} onClick={() => setShowUpgradeView(true)} stretch={false}>
              {intl.formatMessage({ id: 'billing.plan.upgrade', defaultMessage: 'Upgrade Plan' })}
            </Button>
          )}
        </ButtonRow>
      </Frame>

      <PageHeader
        variant={PAGE_HEADER_VARIANT_SMALL}
        title={intl.formatMessage({ id: 'billing.balance.title', defaultMessage: 'Credit Balance' })}
      />
      <Frame>
        <BalanceHeader>
          <BalanceAmount>
            {typeof overview.availableBalance === 'number'
              ? overview.availableBalance.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
              : '0'}
          </BalanceAmount>
          <BalanceUnit>
            {intl.formatMessage({ id: 'billing.balance.unit', defaultMessage: 'credits available' })}
          </BalanceUnit>
        </BalanceHeader>

        {overview.batches.length > 0 && (
          <BatchSection>
            <BatchSectionLabel>
              {intl.formatMessage({ id: 'billing.balance.breakdown', defaultMessage: 'Credit breakdown by batch' })}
            </BatchSectionLabel>
            {[...overview.batches]
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
              .map((batch) => (
                <BatchRow key={`${batch.sourceType}-${batch.createdAt}`}>
                  <BatchLeft>
                    <BatchType>{sourceTypeLabel(batch.sourceType)}</BatchType>
                    <BatchDate>
                      {intl.formatMessage(
                        { id: 'billing.batch.created', defaultMessage: 'Added {date}' },
                        { date: formatDate(batch.createdAt) },
                      )}
                    </BatchDate>
                  </BatchLeft>
                  <BatchInfo>
                    {intl.formatMessage(
                      { id: 'billing.batch.info', defaultMessage: '{remaining} / {original} credits — expires {expiresAt}' },
                      {
                        remaining: batch.remainingAmount.toLocaleString(undefined, { maximumFractionDigits: 2 }),
                        original: batch.originalAmount.toLocaleString(),
                        expiresAt: formatDate(batch.expiresAt),
                      },
                    )}
                  </BatchInfo>
                </BatchRow>
              ))}
          </BatchSection>
        )}
      </Frame>

      <PageHeader
        variant={PAGE_HEADER_VARIANT_SMALL}
        title={intl.formatMessage({ id: 'billing.purchase.title', defaultMessage: 'Buy Additional Credits' })}
      />
      <Frame>
        {!overview.hasStripeSubscription ? (
          <InfoText>
            {intl.formatMessage({
              id: 'billing.purchase.upgradeRequired',
              defaultMessage: 'Upgrade your plan to top up credits.',
            })}
          </InfoText>
        ) : (
          <>
            <SectionDescription>
              {intl.formatMessage(
                { id: 'billing.purchase.description', defaultMessage: 'On-demand credits at ${price}/credit. Credits are valid for 12 months from purchase.' },
                { price: (CREDIT_PRICE_CENTS / 100).toFixed(2) },
              )}
            </SectionDescription>
            {batchLimitReached ? (
              <WarningText>
                {intl.formatMessage(
                  { id: 'billing.purchase.batchLimitReached', defaultMessage: 'You already have {count} active credit batches. Please use your existing credits before purchasing more.' },
                  { count: overview.maxPurchaseBatches },
                )}
              </WarningText>
            ) : (
              <PurchaseForm>
                <QuantityRow>
                  <QuantityLabel>
                    {intl.formatMessage({ id: 'billing.purchase.quantity', defaultMessage: 'Quantity' })}
                  </QuantityLabel>
                  <QuantityInput
                    type="number"
                    min={MIN_QUANTITY}
                    max={MAX_QUANTITY}
                    step={1}
                    value={quantity}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      if (Number.isFinite(val)) setQuantity(val);
                    }}
                  />
                  <QuantityCost>
                    = ${((clampQuantity(quantity) * CREDIT_PRICE_CENTS) / 100).toLocaleString()}
                  </QuantityCost>
                </QuantityRow>
                <InfoText>
                  {intl.formatMessage(
                    { id: 'billing.purchase.quantityRange', defaultMessage: 'Min: {min} credits — Max: {max} credits' },
                    { min: MIN_QUANTITY, max: MAX_QUANTITY.toLocaleString() },
                  )}
                </InfoText>
                <Button
                  variant={BUTTON_VARIANT_PRIMARY}
                  onClick={handlePurchase}
                  disabled={purchasing || !isValidQuantity}
                  stretch={false}
                >
                  {purchasing
                    ? intl.formatMessage({ id: 'billing.purchase.redirecting', defaultMessage: 'Redirecting...' })
                    : intl.formatMessage({ id: 'billing.purchase.button', defaultMessage: 'Purchase Credits' })}
                </Button>
              </PurchaseForm>
            )}
          </>
        )}
      </Frame>

      <PageHeader
        variant={PAGE_HEADER_VARIANT_SMALL}
        title={intl.formatMessage({ id: 'billing.transactions.title', defaultMessage: 'Transaction History' })}
      />
      <Frame>
        <TransactionHistory />
      </Frame>

      <Dialog
        show={showUpgradeView}
        closeOnClickOutside
        closeOnEscape
        onClose={closeUpgradeView}
        onCloseIconButtonClick={closeUpgradeView}
        disableContentPadding
        size={DIALOG_SIZE_VARIANT_MEDIUM}
        dataTestId="upgrade-dialog"
      >
        <PlanPicker showFreeTier={false} />
      </Dialog>
    </Wrapper>
  );
};

export default BillingSettings;
